import TransactionModal from "./transactionModal.js";
import { $_GET } from "../globalFunctions.js";

export default class IssueModal extends TransactionModal {
	constructor(companyId) {
		super("issue-modal", companyId);

		this.VESTING_TYPE_SIMPLE = 1;
		this.formFields = [
			{
				name: "id",
				varType: "int",
			},
			{
				name: "stakeholder",
				varType: "int",
				required: true,
				trigger: "change",
			},
			{
				name: "beneficiary",
				varType: "int",
			},
			{
				name: "price",
				varType: "float",
			},
			{
				name: "investment",
				varType: "float",
			},
			{
				name: "date",
				varType: "string",
				required: true,
			},
			{
				name: "amount",
				varType: "int",
				required: true,
			},
			{
				name: "share_class",
				varType: "int",
				required: true,
			},
			{
				name: "numeration",
				varType: "string",
				mayNotExist: true,
			},
			{
				name: "certificate_number",
				varType: "int",
				mayNotExist: true,

			},
			{
				name: "documents",
				varType: "simple-array",
				type: "int",
			},
			{
				name: "notes",
			},
			{
				name: "vesting_type",
				varType: "int",
				trigger: "change",
			},
			{
				name: "vesting_duration",
				varType: "int",
			},
			{
				name: "vesting_interval",
				varType: "int",
			},
			{
				name: "vesting_cliff",
				varType: "int",
			},
			{
				name: "vesting_date",
				varType: "string",
			},
		];

		this.setupForm();

		Promise.all([
			this.documentsPromise,
			this.setupStakeholders(),
			this.setupShareClasses(),
		]).then(() => {
			this.whenReady();
		});
	}

	whenReady() {
		super.whenReady();

		if (this.currentData?.split_child) {
			const documentsContainer = this.$form.querySelector(
				"[data-container=\"documents\"]",
			);
			const notesContainer = this.$form.querySelector(
				"[data-container=\"notes\"]",
			);

			documentsContainer?.previousElementSibling?.classList?.add("d-none");
			documentsContainer?.classList?.add("d-none");
			notesContainer?.previousElementSibling?.classList?.add("d-none");
			notesContainer?.classList?.add("d-none");
		}
	}

	setupForm() {
		this.$form.querySelector("[name=\"date\"]").valueAsDate = new Date();
		const $vestingType = this.$form.querySelector("[name=\"vesting-type\"]");
		$vestingType.addEventListener("change", () => {
			this.showVestingFields($vestingType.value);
		});

		this.$form
			.querySelector("[data-action=\"autocomplete-numeration\"]")
			?.addEventListener("click", () => {
				this.autocompleteNumeration();
			});

		this.$form
			.querySelector("[data-action=\"autocomplete-shares\"]")
			?.addEventListener("click", () => {
				this.autocompleteShares();
			});
	}

	autocompleteShares() {
		this.setInt("amount", this.getFloat("investment") / this.getFloat("price"));
	}

	autocompleteNumeration() {
		const amount = this.getInt("amount");

		if (amount < 1) {
			this.showErrorOnField("", this.$form.querySelector("[name=\"amount\"]"));
			return;
		}

		this.getLatestNumeration().then((numeration) => {
			let start = numeration + 1;
			let end = start + amount - 1;
			this.$form.querySelector("[name=\"numeration\"]").value = [start, end].join(
				"-",
			);
		});
	}

	showVestingFields(type) {
		type = parseInt(type);

		this.$form.querySelectorAll("[data-vesting-child]").forEach((el) => {
			switch (type) {
				case this.VESTING_TYPE_SIMPLE:
					el.classList.remove("d-none");
					break;
				default:
					el.classList.add("d-none");
					break;
			}
		});
	}

	validateNumerations(numeration, amount) {
		if (!numeration || numeration.length < 1) {
			return true;
		}

		const numerations = numeration.split(";");
		let sum = 0;

		numerations.forEach((numeration) => {
			sum += this.validateNumeration(numeration);
		});

		if (sum !== amount) {
			throw trans("numeration_invalid_shares_amount_doesnt_match", {
				amount: peque.formatNumber(amount),
			});
		}

		return true;
	}

	validateNumeration(numeration) {
		if (!numeration.includes("-")) {
			throw trans("numeration_invalid_format");
		}

		const split = numeration.split("-");

		if (split.length !== 2) {
			throw trans("numeration_invalid_format");
		}
		split[0] = parseInt(split[0]);
		split[1] = parseInt(split[1]);

		if (split.join("-") !== numeration) {
			throw trans("numeration_invalid_format");
		}

		if (split[0] < 1 || split[1] < 1) {
			throw trans("numeration_invalid_min_number");
		}
		if (split[1] < split[0]) {
			throw trans("numeration_invalid_first_number");
		}

		// +1 as count starts from 1 not from 0.
		return split[1] - split[0] + 1;
	}

	validateAndSendForm() {
		this.enableSubmitButton(false);

		const $numerationError = this.$form.querySelector(
			"[data-numeration-error]",
		);
		if ($numerationError) {
			$numerationError.classList.add("d-none");
		}

		this.getFields(this.formFields)
			.then((data) => {
				data.company = this.companyId;
				data.simulation =
					typeof window.pequeData.simulation === "undefined"
						? $_GET("simulation")
						: window.pequeData.simulation;
				data.type = peque.TRANSACTION_TYPE_ISSUE;

				try {
					this.validateNumerations(data.numeration, data.amount);
				} catch (e) {
					this.enableSubmitButton(true);
					if ($numerationError) {
						$numerationError.classList.remove("d-none");
						$numerationError.innerText = e;
					}
					this.showErrorOnField(
						e,
						this.$form.querySelector("[name=\"numeration\"]"),
					);
					return;
				}

				const url = this.currentData && this.currentData.split_child
					? `shares/${data.id}`
					: "transactions";

				peque
					.api(url, JSON.stringify(data), "post")
					.then((response) => {
						this.mutateReactTransactions();
					})
					.catch((error) => {
						this.enableSubmitButton(true);

						if (typeof error.field !== "undefined") {
							if (error.field === "certificate-number") {
								this.showErrorOnField(trans("certificate_number_already_exists"), this.$form.querySelector("[name=\"" + error.field + "\"]"));
							} else {
								this.showErrorOnField(
									error.result,
									this.$form.querySelector("[name=\"" + error.field + "\"]"),
								);
							}
						}
					});
			})
			.catch((error) => {
				this.enableSubmitButton(true);

				if (typeof error.el === "undefined") {
					console.error(error);
					return;
				}
				this.showErrorOnField(error.message, error.el);
			});
	}
}
